import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Grid,
  Divider,
  Pagination,
  MenuItem,
  Menu,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  FormGroup,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Visibility as VisibilityIcon,
  MoreVert as MoreVertIcon,
  Print as PrintIcon,
  Receipt as ReceiptIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  AssignmentReturn as ReturnIcon,
  Close as CloseIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  Update as UpdateIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  RemoveCircle as RemoveCircleIcon,
  Share as ShareIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import Layout from '../../components/layout/Layout';
import salesService from '../../services/salesService';
import PageTitle from '../../components/PageTitle';
import { setPageTitle } from '../../utils/pageTitle';
import companySettingsService from '../../services/companySettingsService';
import printSettingsService from '../../services/printSettingsService';
import { shareBillToWhatsApp } from '../../utils/whatsappShareHelper';
import { formatDate, formatTime } from '../../utils/dateFormatter';

const formatOrderDate = (orderDate) => {
  if (!orderDate) return '';
  // If it's in a numeric format like 202547 or 2025517, parse as YYYYDDD
  if (/^\d{7}$/.test(orderDate)) {
    const year = orderDate.substring(0, 4);
    const dayOfYear = parseInt(orderDate.substring(4), 10);
    const date = new Date(Number(year), 0);
    date.setDate(dayOfYear);
    return formatDate(date);
  }
  if (/^\d{6}$/.test(orderDate)) {
    const year = orderDate.substring(0, 4);
    const dayOfYear = parseInt(orderDate.substring(4), 10);
    const date = new Date(Number(year), 0);
    date.setDate(dayOfYear);
    return formatDate(date);
  }
  return formatDate(orderDate) || orderDate;
};

const formatOrderTime = (createdAt) => {
  if (!createdAt) return '';
  return formatTime(createdAt);
};

const formatCreatedDate = (createdAt) => {
  if (!createdAt) return '';
  return formatDate(createdAt);
};

const ViewOrdersPage = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [orders, setOrders] = useState([]);
  const [nonGstOrders, setNonGstOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [localPage, setLocalPage] = useState(0);
  const [nonGstPage, setNonGstPage] = useState(0);
  const [localRowsPerPage] = useState(10);
  const [pagination, setPagination] = useState({
    page: 0,
    totalPages: 0,
    totalElements: 0,
    size: 10
  });
  const [orderDetailOpen, setOrderDetailOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  // Menu state
  const isMenuOpen = Boolean(menuAnchorEl);

  // State for receipt dialog
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [receiptOrderId, setReceiptOrderId] = useState(null);
  const [selectedReceiptTypes, setSelectedReceiptTypes] = useState({
    CUSTOMER: true,
    OFFICE: false,
    OFFICE_WITH_SIGNATURE: false
  });
  const [enableWhatsappShare, setEnableWhatsappShare] = useState(false);

  // Status dialog state
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedOrderForStatus, setSelectedOrderForStatus] = useState(null);

  // Payment status dialog state
  const [paymentStatusDialogOpen, setPaymentStatusDialogOpen] = useState(false);
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState('');
  const [selectedOrderForPaymentStatus, setSelectedOrderForPaymentStatus] = useState(null);

  // Edit order dialog state
  const [editOrderDialogOpen, setEditOrderDialogOpen] = useState(false);
  const [selectedOrderForEdit, setSelectedOrderForEdit] = useState(null);
  const [editOrderItems, setEditOrderItems] = useState([]);
  const [editOrderLoading, setEditOrderLoading] = useState(false);

  // View mode state for filtering order list tabs
  const [viewMode, setViewMode] = useState('all'); // 'all', 'quotations'
  // Hidden mode: Ctrl+Q shows only non-GST bills (similar to GSTR1 behavior)
  const [showNonGstBillsOnly, setShowNonGstBillsOnly] = useState(false);

  // Set page title on component mount
  useEffect(() => {
    setPageTitle("Sales Orders");
  }, []);

  // Fetch orders with pagination
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      // Call the API to get all sales orders with pagination
      const result = await salesService.getAllSalesOrders({
        page: pagination.page,
        size: pagination.size,
        sort: 'createdAt',
        direction: 'DESC',
        excludeStatus: 'RETURNED' // Ensure backend filters out returns
      });

      // Set orders and pagination directly from backend response
      setOrders(result.content || []);
      setPagination({
        ...pagination,
        totalPages: result.totalPages || 1,
        totalElements: result.totalElements || 0
      });

    } catch (error) {
      console.error('Error fetching orders:', error);
      enqueueSnackbar('Failed to load orders: ' + (error.message || 'Unknown error'), { variant: 'error' });

      // Fallback to mock data for development/testing purposes
      console.warn('Falling back to mock data due to API error');
      const mockOrders = [
        {
          id: 1001,
          customerId: 1,
          customerName: "John Doe",
          orderDate: "2023-06-15",
          status: "DELIVERED",
          paymentStatus: "PAID",
          paymentMethod: "CASH",
          subtotal: 156.99,
          taxAmount: 12.56,
          discount: 0,
          total: 169.55,
          items: [
            { productId: 1, productName: "Apple", quantity: 5, unitPrice: 2.99, total: 14.95 },
            { productId: 2, productName: "Banana", quantity: 2, unitPrice: 1.50, total: 3.00 }
          ]
        },
        {
          id: 1002,
          customerId: 2,
          customerName: "Jane Smith",
          orderDate: "2023-06-16",
          status: "SHIPPED",
          paymentStatus: "PAID",
          paymentMethod: "CREDIT_CARD",
          subtotal: 89.97,
          taxAmount: 7.20,
          discount: 5.00,
          total: 92.17,
          items: [
            { productId: 3, productName: "Orange Juice", quantity: 3, unitPrice: 4.99, total: 14.97 },
            { productId: 4, productName: "Milk", quantity: 1, unitPrice: 3.50, total: 3.50 }
          ]
        },
        {
          id: 1003,
          customerId: 3,
          customerName: "Bob Johnson",
          orderDate: "2023-06-17",
          status: "PENDING",
          paymentStatus: "PENDING",
          paymentMethod: "CHEQUE",
          subtotal: 249.99,
          taxAmount: 20.00,
          discount: 10.00,
          total: 259.99,
          items: [
            { productId: 5, productName: "Laptop Stand", quantity: 1, unitPrice: 49.99, total: 49.99 },
            { productId: 6, productName: "Wireless Mouse", quantity: 2, unitPrice: 29.99, total: 59.98 }
          ]
        }
      ];

      setOrders(mockOrders);
      setFilteredOrders(mockOrders);

      setPagination({
        ...pagination,
        totalPages: 1,
        totalElements: mockOrders.length
      });
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, enqueueSnackbar]);

  const fetchNonGstOrders = useCallback(async () => {
    setLoading(true);
    try {
      const pageSize = 200;
      const firstPage = await salesService.getAllSalesOrders({
        page: 0,
        size: pageSize,
        sort: 'createdAt',
        direction: 'DESC',
        excludeStatus: 'RETURNED'
      });

      const all = [...(firstPage.content || [])];
      const totalPages = firstPage.totalPages || 1;

      for (let page = 1; page < totalPages; page += 1) {
        const res = await salesService.getAllSalesOrders({
          page,
          size: pageSize,
          sort: 'createdAt',
          direction: 'DESC',
          excludeStatus: 'RETURNED'
        });
        all.push(...(res.content || []));
      }

      setNonGstOrders(all);
    } catch (error) {
      console.error('Error fetching non-GST orders:', error);
      enqueueSnackbar('Failed to load non-GST orders: ' + (error.message || 'Unknown error'), { variant: 'error' });
      setNonGstOrders([]);
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  // Load print settings (including WhatsApp share) for current location
  useEffect(() => {
    const loadSettings = async () => {
      try {
        // Get current user to determine location
        const userStr = localStorage.getItem('user');
        let locationId = null;
        if (userStr) {
          try {
            const user = JSON.parse(userStr);
            locationId = user.location?.id;
          } catch (e) {
            console.error('Error parsing user from localStorage', e);
          }
        }

        const settings = await printSettingsService.getPrintSettings(locationId);
        if (settings && typeof settings.enableWhatsappShare === 'boolean') {
          setEnableWhatsappShare(settings.enableWhatsappShare);
        }
      } catch (error) {
        console.error('Error loading print settings:', error);
        // Don't show error toast, just use default value
      }
    };

    loadSettings();
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    if (!showNonGstBillsOnly) {
      return;
    }
    setNonGstPage(0);
    fetchNonGstOrders();
  }, [showNonGstBillsOnly, fetchNonGstOrders]);

  // Ctrl+Q toggles hidden non-GST bills list mode
  useEffect(() => {
    const handleKeyDown = (e) => {
      const key = String(e.key || '').toLowerCase();
      if (e.ctrlKey && key === 'q') {
        e.preventDefault();
        setLocalPage(0);
        setNonGstPage(0);
        setShowNonGstBillsOnly((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Filter orders based on search query and view mode
  useEffect(() => {
    // Reset local page when search or view mode changes
    setLocalPage(0);
    setNonGstPage(0);

    const sourceOrders = showNonGstBillsOnly ? nonGstOrders : orders;
    let baseFiltered = sourceOrders.filter(order =>
      order.status !== 'RETURNED' &&
      order.status !== 'CANCELLED' &&
      !(order.orderNumber && order.orderNumber.startsWith('RETURN-'))
    );

    // Non-GST bills must stay hidden by default; only Ctrl+Q reveals them
    if (showNonGstBillsOnly) {
      baseFiltered = baseFiltered.filter(order =>
        (!order.isQuotation || order.isQuotation === false) &&
        order.nonGst === true
      );
    } else {
      baseFiltered = baseFiltered.filter(order => order.nonGst !== true);
    }

    // Apply view mode filter
    if (viewMode === 'quotations') {
      baseFiltered = baseFiltered.filter(order => order.isQuotation === true);
    }
    // viewMode === 'all' - no additional filter beyond non-GST visibility mode

    if (!searchQuery.trim()) {
      setFilteredOrders(baseFiltered);
      return;
    }

    const lowerQuery = searchQuery.toLowerCase();
    const filtered = baseFiltered.filter(order =>
    // Apply search filters
    (order.id.toString().includes(lowerQuery) ||
      (order.customerName && order.customerName.toLowerCase().includes(lowerQuery)) ||
      (order.customer?.name && order.customer.name.toLowerCase().includes(lowerQuery)) ||
      (order.orderNumber && order.orderNumber.toLowerCase().includes(lowerQuery)) ||
      (order.displayId && order.displayId.toLowerCase().includes(lowerQuery)) ||
      order.status.toLowerCase().includes(lowerQuery) ||
      order.paymentStatus.toLowerCase().includes(lowerQuery) ||
      order.paymentMethod.toLowerCase().includes(lowerQuery))
    );

    setFilteredOrders(filtered);
  }, [orders, nonGstOrders, searchQuery, viewMode, showNonGstBillsOnly]);

  // Handle search input
  const handleSearch = (e) => {
    e.preventDefault();
    // Already handled by the useEffect
  };

  // Handle order selection
  const handleViewOrder = async (order) => {
    // If the order doesn't have items, fetch them
    if (!order.items || order.items.length === 0) {
      setLoading(true);
      try {
        // Fetch order details including items
        const fullOrderData = await salesService.getSalesOrderById(order.id);

        // If the backend returns order with items
        if (fullOrderData && fullOrderData.items) {
          setSelectedOrder(fullOrderData);
        } else {
          // If we need to fetch items separately
          const items = await salesService.getSalesOrderItems(order.id);

          // Format the items to match the expected structure
          setSelectedOrder({
            ...order,
            items: items.map(item => ({
              ...item,
              // Create a product object if it doesn't exist
              product: item.product || {
                id: item.productId,
                name: item.productName || `Product ID: ${item.productId}`
              }
            }))
          });
        }
      } catch (error) {
        console.error(`Error fetching details for order ${order.id}:`, error);
        enqueueSnackbar(`Failed to load order details: ${error.message || 'Unknown error'}`, {
          variant: 'error'
        });

        // Use what we have
        setSelectedOrder(order);
      } finally {
        setLoading(false);
      }
    } else {
      // Order already has items, use it directly
      setSelectedOrder(order);
    }

    setOrderDetailOpen(true);
  };

  // Handle menu open
  const handleMenuOpen = (event, orderId) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedOrderId(orderId);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedOrderId(null);
  };

  // Handle proceed to bill (convert quotation to bill)
  const handleProceedToBill = (quotation) => {
    // Store quotation data in localStorage to load in POS
    localStorage.setItem('quotationToLoad', JSON.stringify(quotation));
    // Navigate to POS Keyboard page
    navigate('/sales/pos-keyboard');
  };

  // Handle WhatsApp share
  const handleShareWhatsApp = async () => {
    const order = orders.find(o => o.id === selectedOrderId);
    if (!order) {
      enqueueSnackbar('Order not found', { variant: 'error' });
      handleMenuClose();
      return;
    }

    try {
      await shareBillToWhatsApp(
        order.id,
        order.customer || (order.customerName ? { name: order.customerName } : null),
        order.orderNumber || order.id
      );
    } catch (error) {
      console.error('Error sharing to WhatsApp:', error);
      enqueueSnackbar('Failed to share bill to WhatsApp', { variant: 'error' });
    } finally {
      handleMenuClose();
    }
  };

  // Handle pagination change
  const handlePageChange = (event, value) => {
    if (searchQuery.trim() || showNonGstBillsOnly) {
      // Client-side pagination for filtered results
      if (showNonGstBillsOnly) {
        setNonGstPage(value - 1);
      } else {
        setLocalPage(value - 1);
      }
    } else {
      // Server-side pagination for all orders
      setPagination({
        ...pagination,
        page: value - 1 // MUI pagination is 1-based, backend is 0-based
      });
    }
  };

  const useClientPagination = searchQuery.trim() || showNonGstBillsOnly;
  const currentClientPage = showNonGstBillsOnly ? nonGstPage : localPage;
  const totalPageCount = useClientPagination
    ? Math.max(1, Math.ceil(filteredOrders.length / localRowsPerPage))
    : Math.max(1, pagination.totalPages || 1);

  // Status chip color mapping
  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING': return 'warning';
      case 'PROCESSING': return 'info';
      case 'SHIPPED': return 'primary';
      case 'DELIVERED': return 'success';
      case 'CANCELLED': return 'error';
      default: return 'default';
    }
  };

  // Payment status chip color mapping
  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'PAID': return 'success';
      case 'PENDING': return 'warning';
      case 'FAILED': return 'error';
      case 'REFUNDED': return 'info';
      default: return 'default';
    }
  };

  // Payment method display formatting (handles single method or split bill JSON)
  const formatPaymentMethod = (method) => {
    if (!method) return 'Unknown';
    if (typeof method !== 'string') return String(method);

    // Split bill: JSON with payments array
    if (method.trim().startsWith('{')) {
      try {
        const data = JSON.parse(method);
        const payments = data?.payments || [];
        if (payments.length === 0) return 'Split Bill';
        const labels = { CASH: 'Cash', PAYNOW: 'UPI', CREDIT_CARD: 'Card', DEBIT_CARD: 'Card', CREDIT: 'Credit', BANK_TRANSFER: 'Bank', CHEQUE: 'Cheque' };
        return 'Split: ' + payments.map((p) => `${labels[p.mode] || p.mode} ₹${Number(p.amount || 0).toFixed(2)}`).join(', ');
      } catch (e) {
        return 'Split Bill';
      }
    }

    // Single payment: use display labels so PAYNOW shows as UPI
    const singleLabels = { CASH: 'Cash', PAYNOW: 'UPI', CREDIT_CARD: 'Card', DEBIT_CARD: 'Card', CREDIT: 'Credit', BANK_TRANSFER: 'Bank', CHEQUE: 'Cheque' };
    const upper = (method || '').trim().toUpperCase();
    return singleLabels[upper] != null ? singleLabels[upper] : method.split('_').map((word) => word.charAt(0) + word.slice(1).toLowerCase()).join(' ');
  };

  // Handle opening receipt dialog - NOW DIRECTLY DOWNLOADS INVOICE (CUSTOMER COPY)
  // Dialog code is commented below for future use
  const handleOpenReceiptDialog = async (orderId) => {
    handleMenuClose();

    try {
      setLoading(true);

      // Default to CUSTOMER copy - directly download without dialog (no header text)
      const receiptBlob = await salesService.generateReceipt(orderId, 'CUSTOMER', null);

      // Create URL for the blob
      const url = window.URL.createObjectURL(receiptBlob);

      // Prepare filename for download
      const fileName = `Invoice-${orderId}.pdf`;

      // Create a hidden anchor element for downloading
      const downloadLink = document.createElement('a');
      downloadLink.href = url;
      downloadLink.download = fileName;
      downloadLink.target = '_blank';

      // Add to DOM, click, then remove
      document.body.appendChild(downloadLink);

      // First open for viewing in new tab
      window.open(url, '_blank');

      // Then trigger download
      downloadLink.click();
      document.body.removeChild(downloadLink);

      // Show success message
      enqueueSnackbar('Invoice downloaded successfully', { variant: 'success' });

      // Cleanup
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error(`Error generating receipt for order ${orderId}:`, error);
      enqueueSnackbar('Failed to generate invoice', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle receipt type change
  const handleReceiptTypeChange = (event) => {
    setSelectedReceiptTypes({
      ...selectedReceiptTypes,
      [event.target.name]: event.target.checked
    });
  };

  // Handle Print Receipt
  const handlePrintReceipt = async () => {
    if (!receiptOrderId) return;

    try {
      setLoading(true);

      // Get selected receipt types
      const receiptTypes = Object.entries(selectedReceiptTypes)
        .filter(([_, isSelected]) => isSelected)
        .map(([type]) => type);

      if (receiptTypes.length === 0) {
        enqueueSnackbar('Please select at least one receipt type', { variant: 'warning' });
        return;
      }

      if (receiptTypes.length === 1) {
        // Single receipt type
        const type = receiptTypes[0];

        // Define custom header based on receipt type
        let customHeader;
        switch (type) {
          case 'CUSTOMER':
            customHeader = null; // Removed 'CUSTOMER COPY' - use default title
            break;
          case 'OFFICE':
            customHeader = null; // Removed 'OFFICE COPY' - use default title
            break;
          case 'OFFICE_WITH_SIGNATURE':
            customHeader = null; // Removed 'OFFICE COPY - CUSTOMER SIGNATURE REQUIRED' - use default title
            break;
          default:
            customHeader = null;
        }

        const receiptBlob = await salesService.generateReceipt(receiptOrderId, type, customHeader);

        // Create URL for the blob
        const url = window.URL.createObjectURL(receiptBlob);

        // Prepare filename for download
        const fileName = `Invoice-${receiptOrderId}.pdf`;

        // Create a hidden anchor element for downloading
        const downloadLink = document.createElement('a');
        downloadLink.href = url;
        downloadLink.download = fileName;
        downloadLink.target = '_blank';

        // Add to DOM, click, then remove
        document.body.appendChild(downloadLink);

        // First open for viewing in new tab
        window.open(url, '_blank');

        // Then trigger download
        downloadLink.click();
        document.body.removeChild(downloadLink);

        // Show success message
        enqueueSnackbar('Invoice downloaded successfully', { variant: 'success' });

        // Cleanup
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 100);
      } else {
        // Multiple receipt types
        const receipts = await salesService.generateMultipleReceipts(receiptOrderId, receiptTypes);

        // Generate display names for receipt types
        const getReceiptName = (type) => {
          switch (type) {
            case 'CUSTOMER': return 'Customer';
            case 'OFFICE': return 'Office';
            case 'OFFICE_WITH_SIGNATURE': return 'Office_with_Signature';
            default: return type;
          }
        };

        // Open each receipt in a new tab
        receipts.forEach(({ type, blob }) => {
          const fileName = `Invoice-${receiptOrderId}-${getReceiptName(type)}.pdf`;
          const url = window.URL.createObjectURL(blob);

          // Create a hidden anchor element for downloading
          const downloadLink = document.createElement('a');
          downloadLink.href = url;
          downloadLink.download = fileName;
          downloadLink.target = '_blank';

          // Add to DOM, click, then remove
          document.body.appendChild(downloadLink);

          // Open for viewing in new tab and trigger download
          window.open(url, '_blank');
          downloadLink.click();
          document.body.removeChild(downloadLink);

          // Clean up the URL
          setTimeout(() => window.URL.revokeObjectURL(url), 100);
        });

        // Show success message
        enqueueSnackbar(`${receipts.length} invoices downloaded successfully`, { variant: 'success' });
      }

      // Close dialog
      setReceiptDialogOpen(false);
    } catch (error) {
      console.error(`Error generating receipt(s) for order ${receiptOrderId}:`, error);
      enqueueSnackbar('Failed to generate receipt(s)', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Function to navigate to POS page
  const handleAddNewOrder = () => {
    navigate('/sales/pos');
  };

  // Function to navigate to returns page
  const handleProcessReturn = (orderId) => {
    navigate(`/sales/returns?orderId=${orderId}`);
    handleMenuClose();
  };

  // Handle opening status dialog
  const handleOpenStatusDialog = (order) => {
    setSelectedOrderForStatus(order);
    setSelectedStatus(order.status);
    setStatusDialogOpen(true);
  };

  // Handle closing status dialog
  const handleCloseStatusDialog = () => {
    setStatusDialogOpen(false);
    setSelectedStatus('');
    setSelectedOrderForStatus(null);
  };

  // Handle updating order status
  const handleUpdateStatus = async () => {
    if (!selectedOrderForStatus || !selectedStatus) return;

    setLoading(true);
    try {
      // Update the status in the backend
      await salesService.updateSalesOrderStatus(selectedOrderForStatus.id, selectedStatus);

      // Update the local state immediately for better UX
      setOrders(prevOrders =>
        prevOrders.map(order =>
          order.id === selectedOrderForStatus.id
            ? { ...order, status: selectedStatus }
            : order
        )
      );

      // Update filtered orders as well
      setFilteredOrders(prevFiltered =>
        prevFiltered.map(order =>
          order.id === selectedOrderForStatus.id
            ? { ...order, status: selectedStatus }
            : order
        )
      );

      enqueueSnackbar(`Order #${selectedOrderForStatus.id} status updated to ${selectedStatus}`, {
        variant: 'success',
        autoHideDuration: 3000
      });

      handleCloseStatusDialog();

      // Refresh the orders list to ensure data consistency
      await fetchOrders();
    } catch (error) {
      console.error('Error updating order status:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Failed to update order status. Please try again.',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle updating payment status
  const handleUpdatePaymentStatus = async () => {
    if (!selectedOrderForPaymentStatus || !selectedPaymentStatus) return;

    setLoading(true);
    try {
      // Update the payment status in the backend
      await salesService.updateSalesOrderPaymentStatus(selectedOrderForPaymentStatus.id, selectedPaymentStatus);

      // Update the local state immediately for better UX
      setOrders(prevOrders =>
        prevOrders.map(order =>
          order.id === selectedOrderForPaymentStatus.id
            ? { ...order, paymentStatus: selectedPaymentStatus }
            : order
        )
      );

      // Update filtered orders as well
      setFilteredOrders(prevFiltered =>
        prevFiltered.map(order =>
          order.id === selectedOrderForPaymentStatus.id
            ? { ...order, paymentStatus: selectedPaymentStatus }
            : order
        )
      );

      enqueueSnackbar(`Order #${selectedOrderForPaymentStatus.id} payment status updated to ${selectedPaymentStatus}`, {
        variant: 'success',
        autoHideDuration: 3000
      });

      handleClosePaymentStatusDialog();

      // Refresh the orders list to ensure data consistency
      await fetchOrders();
    } catch (error) {
      console.error('Error updating payment status:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Failed to update payment status. Please try again.',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle opening payment status dialog
  const handleOpenPaymentStatusDialog = (order) => {
    setSelectedOrderForPaymentStatus(order);
    setSelectedPaymentStatus(order.paymentStatus);
    setPaymentStatusDialogOpen(true);
  };

  // Handle closing payment status dialog
  const handleClosePaymentStatusDialog = () => {
    setPaymentStatusDialogOpen(false);
    setSelectedPaymentStatus('');
    setSelectedOrderForPaymentStatus(null);
  };

  // Handle opening edit order - navigate to POS Keyboard with order pre-loaded
  const handleOpenEditOrderDialog = (order) => {
    try {
      // Store only the order ID; POS Keyboard will fetch full details
      localStorage.setItem('orderToEditId', String(order.id));
      // Ensure any existing edit dialog state is closed
      setEditOrderDialogOpen(false);
      // Navigate to POS Keyboard page for full editing experience
      navigate('/sales/pos-keyboard');
    } catch (error) {
      console.error('Error preparing order for edit:', error);
      enqueueSnackbar('Failed to open order for editing', { variant: 'error' });
    } finally {
      setEditOrderLoading(false);
    }
  };

  // Handle editing item quantity
  const handleEditItemQuantity = (index, newQuantity) => {
    const updatedItems = [...editOrderItems];
    updatedItems[index] = {
      ...updatedItems[index],
      quantity: Math.max(0, newQuantity) // Ensure quantity is not negative
    };
    setEditOrderItems(updatedItems);
  };

  // Handle deleting an item
  const handleDeleteItem = (index) => {
    const updatedItems = editOrderItems.filter((_, i) => i !== index);
    setEditOrderItems(updatedItems);
  };

  // Calculate edit subtotal
  const calculateEditSubtotal = () => {
    return editOrderItems.reduce((sum, item) => {
      return sum + ((item.unitPrice || 0) * (item.quantity || 0));
    }, 0);
  };

  // Calculate edit tax (returns 0 for edit form - no tax calculation)
  const calculateEditTax = () => {
    return 0;
  };

  // Calculate edit total (no tax calculation for edit form)
  const calculateEditTotal = () => {
    return calculateEditSubtotal();
  };

  // Handle saving edited order
  const handleSaveEditOrder = async () => {
    if (!selectedOrderForEdit || editOrderItems.length === 0) {
      enqueueSnackbar('Cannot save order with no items', { variant: 'error' });
      return;
    }

    try {
      setEditOrderLoading(true);

      // Prepare the update request
      const updateRequest = {
        customerId: selectedOrderForEdit.customer?.id || selectedOrderForEdit.customerId,
        companyId: selectedOrderForEdit.company?.id || selectedOrderForEdit.companyId,
        orderDate: selectedOrderForEdit.orderDate,
        paymentMethod: selectedOrderForEdit.paymentMethod,
        paymentStatus: selectedOrderForEdit.paymentStatus,
        status: selectedOrderForEdit.status,
        notes: selectedOrderForEdit.notes,
        discount: selectedOrderForEdit.discount || 0,
        shippingCost: selectedOrderForEdit.shippingCost || 0,
        items: editOrderItems.map(item => ({
          productId: item.product?.id || item.productId,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          taxId: item.tax?.id || item.taxId,
          discount: item.discount || 0
        }))
      };

      // Update the order
      await salesService.updateSalesOrder(selectedOrderForEdit.id, updateRequest);

      enqueueSnackbar('Order updated successfully!', { variant: 'success' });

      // Close dialog and refresh orders
      setEditOrderDialogOpen(false);
      setSelectedOrderForEdit(null);
      setEditOrderItems([]);

      // Refresh the orders list
      fetchOrders();

    } catch (error) {
      console.error('Error updating order:', error);
      enqueueSnackbar(
        `Failed to update order: ${error.response?.data?.message || error.message || 'Unknown error'}`,
        { variant: 'error' }
      );
    } finally {
      setEditOrderLoading(false);
    }
  };

  return (
    <Layout>
      <PageTitle title="Sales Orders" />
      <Box
        sx={{
          p: 3,
          width: '100%',
          bgcolor: showNonGstBillsOnly ? '#e8f5e9' : 'transparent',
          borderRadius: 1
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2 }}>
          <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
            <ReceiptIcon sx={{ mr: 1, color: showNonGstBillsOnly ? '#00695c' : 'inherit' }} />
            Sales Orders
          </Typography>

          {/* Search bar moved here */}
          <Box component="form" onSubmit={handleSearch} sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, maxWidth: '600px' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search order ID, customer, status..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchQuery('')}
                      edge="end"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button type="submit" variant="contained" size="medium" sx={{ ml: 1 }}>
              Search
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, whiteSpace: 'nowrap' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddNewOrder}
            >
              Add Order
            </Button>
            <Button
              variant="outlined"
              color="info"
              startIcon={<ReturnIcon />}
              onClick={() => navigate('/sales/view-returns')}
            >
              View Returns
            </Button>
          </Box>
        </Box>

        {searchQuery.trim() && (
          <Box sx={{ mb: 2, px: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Search results for: "{searchQuery}"
            </Typography>
            <Button
              size="small"
              onClick={() => setSearchQuery('')}
              startIcon={<ClearIcon />}
              sx={{ minWidth: 'auto' }}
            >
              Clear
            </Button>
          </Box>
        )}

        {showNonGstBillsOnly && (
          <Box sx={{ mb: 2, px: 2 }}>
            
          </Box>
        )}

        {/* View Mode Tabs */}
        <Paper sx={{ mb: 3, border: showNonGstBillsOnly ? '1px solid #00796b' : undefined }}>
          <Tabs
            value={viewMode}
            onChange={(e, newValue) => setViewMode(newValue)}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              ...(showNonGstBillsOnly && {
                bgcolor: '#e0f2f1',
                '& .MuiTab-root': { color: '#004d40', fontWeight: 600 },
                '& .Mui-selected': { color: '#00695c !important' },
                '& .MuiTabs-indicator': { backgroundColor: '#00796b' }
              })
            }}
          >
            <Tab label="All Orders" value="all" />
            <Tab label="Quotations" value="quotations" />
          </Tabs>
        </Paper>

        {/* Loading indicator */}
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {/* Orders table */}
        <TableContainer component={Paper} sx={{ border: showNonGstBillsOnly ? '1px solid #00796b' : undefined }}>
          <Table>
            <TableHead>
              <TableRow sx={showNonGstBillsOnly ? { bgcolor: '#006064' } : undefined}>
                <TableCell sx={showNonGstBillsOnly ? { color: 'white', fontWeight: 'bold' } : undefined}>S.No</TableCell>
                <TableCell sx={showNonGstBillsOnly ? { color: 'white', fontWeight: 'bold' } : undefined}>Location</TableCell>
                <TableCell sx={showNonGstBillsOnly ? { color: 'white', fontWeight: 'bold' } : undefined}>Order ID</TableCell>
                <TableCell sx={showNonGstBillsOnly ? { color: 'white', fontWeight: 'bold' } : undefined}>Date</TableCell>
                <TableCell sx={showNonGstBillsOnly ? { color: 'white', fontWeight: 'bold' } : undefined}>Time</TableCell>
                <TableCell sx={showNonGstBillsOnly ? { color: 'white', fontWeight: 'bold' } : undefined}>Created Date</TableCell>
                <TableCell sx={showNonGstBillsOnly ? { color: 'white', fontWeight: 'bold' } : undefined}>Customer</TableCell>
                <TableCell sx={showNonGstBillsOnly ? { color: 'white', fontWeight: 'bold' } : undefined}>Status</TableCell>
                <TableCell sx={showNonGstBillsOnly ? { color: 'white', fontWeight: 'bold' } : undefined}>Payment</TableCell>
                <TableCell align="right" sx={showNonGstBillsOnly ? { color: 'white', fontWeight: 'bold' } : undefined}>Total</TableCell>
                <TableCell align="center" sx={showNonGstBillsOnly ? { color: 'white', fontWeight: 'bold' } : undefined}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOrders.length > 0 ? (
                filteredOrders
                  .slice(currentClientPage * localRowsPerPage, currentClientPage * localRowsPerPage + localRowsPerPage)
                  .map((order, idx) => (
                    <TableRow
                      key={order.id}
                      sx={showNonGstBillsOnly ? { backgroundColor: idx % 2 === 0 ? '#f1f8e9' : '#dcedc8' } : undefined}
                    >
                      <TableCell>{(useClientPagination ? currentClientPage : pagination.page) * (useClientPagination ? localRowsPerPage : pagination.size) + idx + 1}</TableCell>
                      <TableCell>{order.location?.name || 'N/A'}</TableCell>
                      <TableCell>{order.displayId || order.orderNumber || `#${order.id}`}</TableCell>
                      <TableCell>{formatOrderDate(order.orderDate)}</TableCell>
                      <TableCell>{formatOrderTime(order.createdAt)}</TableCell>
                      <TableCell>{formatCreatedDate(order.createdAt)}</TableCell>
                      <TableCell>{order.customer?.name || order.customerName || 'Guest'}</TableCell>
                      <TableCell>
                        {order.isQuotation ? (
                          // For quotations, only show quotation badge
                          <Chip
                            label="QUOTATION"
                            color="warning"
                            size="small"
                          />
                        ) : (
                          // For normal bills, show status
                          <Chip
                            label={order.status}
                            color={getStatusColor(order.status)}
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Chip
                            label={order.paymentStatus}
                            color={getPaymentStatusColor(order.paymentStatus)}
                            size="small"
                            sx={{ mb: 0.5 }}
                          />
                          <Typography variant="caption">
                            {formatPaymentMethod(order.paymentMethod)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        ₹{order.total.toFixed(2)}
                      </TableCell>
                      <TableCell align="center">
                        {order.isQuotation ? (
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => handleProceedToBill(order)}
                            sx={{ mr: 1 }}
                          >
                            Proceed to Bill
                          </Button>
                        ) : null}
                        <IconButton
                          size="small"
                          onClick={() => handleViewOrder(order)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, order.id)}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography variant="body1" sx={{ py: 2 }}>
                      No orders found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination Controls */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, px: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {showNonGstBillsOnly ? (
              `Showing ${Math.min(currentClientPage * localRowsPerPage + 1, filteredOrders.length)}-${Math.min((currentClientPage + 1) * localRowsPerPage, filteredOrders.length)} of ${filteredOrders.length} non-GST records`
            ) : useClientPagination ? (
              `Showing ${Math.min(currentClientPage * localRowsPerPage + 1, filteredOrders.length)}-${Math.min((currentClientPage + 1) * localRowsPerPage, filteredOrders.length)} of ${filteredOrders.length} filtered results (${pagination.totalElements} total records)`
            ) : (
              `Showing ${Math.min(pagination.page * pagination.size + 1, pagination.totalElements)}-${Math.min((pagination.page + 1) * pagination.size, pagination.totalElements)} of ${pagination.totalElements} records`
            )}
          </Typography>
          {totalPageCount > 1 && (
            <Pagination
              count={totalPageCount}
              page={useClientPagination ? currentClientPage + 1 : pagination.page + 1}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          )}
        </Box>

        {/* Order Detail Dialog */}
        <Dialog
          open={orderDetailOpen}
          onClose={() => setOrderDetailOpen(false)}
          fullWidth
          maxWidth="md"
        >
          {selectedOrder && (
            <>
              <DialogTitle sx={{ pb: 0 }}>
                Order {selectedOrder.displayId || selectedOrder.orderNumber || `#${selectedOrder.id}`}
              </DialogTitle>
              <Box sx={{ px: 3, pb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {formatOrderDate(selectedOrder.orderDate)}
                </Typography>
              </Box>
              <DialogContent dividers>
                <Grid container spacing={3}>
                  {/* Order Info */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6">Order Information</Typography>
                    <Box sx={{ mt: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Status</Typography>
                          <Chip
                            label={selectedOrder.status}
                            color={getStatusColor(selectedOrder.status)}
                            size="small"
                            sx={{ mt: 0.5 }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Payment Status</Typography>
                          <Chip
                            label={selectedOrder.paymentStatus}
                            color={getPaymentStatusColor(selectedOrder.paymentStatus)}
                            size="small"
                            sx={{ mt: 0.5 }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">Payment Method</Typography>
                          <Typography variant="body1">
                            {formatPaymentMethod(selectedOrder.paymentMethod)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>

                  {/* Customer Info */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6">Customer Information</Typography>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">Name</Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        {selectedOrder.customer?.name || selectedOrder.customerName || 'Guest Customer'}
                      </Typography>

                      <Typography variant="body2" color="text.secondary">Email</Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        {selectedOrder.customer && selectedOrder.customer.email
                          ? selectedOrder.customer.email
                          : 'N/A'}
                      </Typography>

                      <Typography variant="body2" color="text.secondary">Phone</Typography>
                      <Typography variant="body1">
                        {selectedOrder.customer && selectedOrder.customer.phone
                          ? selectedOrder.customer.phone
                          : 'N/A'}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Order Items */}
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Order Items</Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Product</TableCell>
                            <TableCell align="right">Price</TableCell>
                            <TableCell align="right">Quantity</TableCell>
                            <TableCell align="right">Discount</TableCell>
                            <TableCell align="right">Tax</TableCell>
                            <TableCell align="right">Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedOrder.items.map((item, index) => (
                            <TableRow key={index}>
                              <TableCell>{item.product ? item.product.name : `Product ID: ${item.productId || 'Unknown'}`}</TableCell>
                              <TableCell align="right">₹{item.unitPrice ? item.unitPrice.toFixed(2) : '0.00'}</TableCell>
                              <TableCell align="right">{item.quantity}</TableCell>
                              <TableCell align="right">
                                {item.discount && item.discount > 0
                                  ? `₹${item.discount.toFixed(2)}`
                                  : '-'}
                              </TableCell>
                              <TableCell align="right">
                                {item.tax
                                  ? `${item.tax.name || ''} (${item.tax.rate ? (item.tax.rate * 100).toFixed(1) : '0'}%): ₹${item.taxAmount ? item.taxAmount.toFixed(2) : '0.00'}`
                                  : item.taxAmount > 0
                                    ? `₹${item.taxAmount.toFixed(2)}`
                                    : '-'}
                              </TableCell>
                              <TableCell align="right">₹{item.total.toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>

                  {/* Order Summary */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', mt: 2 }}>
                      <Typography variant="h6" sx={{ alignSelf: 'flex-start', mb: 1 }}>Order Calculation</Typography>

                      {/* Item subtotal */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '350px', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">Item Subtotal:</Typography>
                        <Typography variant="body1">₹{selectedOrder.subtotal.toFixed(2)}</Typography>
                      </Box>

                      {/* Item discounts */}
                      {selectedOrder.items && selectedOrder.items.some(item => item.discount > 0) && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '350px', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">Item Discounts:</Typography>
                          <Typography variant="body1" color="error">
                            -₹{selectedOrder.items.reduce((sum, item) => sum + (item.discount || 0), 0).toFixed(2)}
                          </Typography>
                        </Box>
                      )}

                      {/* Order discount */}
                      {selectedOrder.discount > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '350px', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">Order Discount:</Typography>
                          <Typography variant="body1" color="error">
                            -₹{selectedOrder.discount.toFixed(2)}
                          </Typography>
                        </Box>
                      )}

                      {/* Tax amount */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '350px', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Tax Amount:
                        </Typography>
                        <Typography variant="body1">₹{selectedOrder.taxAmount.toFixed(2)}</Typography>
                      </Box>

                      {/* Shipping cost */}
                      {selectedOrder.shippingCost > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '350px', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">Shipping Cost:</Typography>
                          <Typography variant="body1">₹{selectedOrder.shippingCost.toFixed(2)}</Typography>
                        </Box>
                      )}

                      <Divider sx={{ width: '350px', my: 1 }} />

                      {/* Total */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '350px' }}>
                        <Typography variant="h6">Total:</Typography>
                        <Typography variant="h6" color="primary.main">
                          ₹{selectedOrder.total.toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOrderDetailOpen(false)}>Close</Button>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  color="primary"
                  onClick={() => handleOpenReceiptDialog(selectedOrder.id)}
                >
                  Download Invoice
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* Actions Menu */}
        <Menu
          anchorEl={menuAnchorEl}
          open={isMenuOpen}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => {
            const order = orders.find(o => o.id === selectedOrderId);
            handleMenuClose();
            handleViewOrder(order);
          }}>
            <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
            View Details
          </MenuItem>
          <MenuItem
            onClick={() => {
              const order = orders.find(o => o.id === selectedOrderId);
              handleMenuClose();
              if (order) handleOpenEditOrderDialog(order);
            }}
            disabled={(() => {
              const order = orders.find(o => o.id === selectedOrderId);
              // Allow editing for all orders except CANCELLED
              return order?.status === 'CANCELLED';
            })()}
          >
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edit Order
          </MenuItem>
          <MenuItem onClick={() => {
            const order = orders.find(o => o.id === selectedOrderId);
            handleMenuClose();
            if (order) handleOpenReceiptDialog(order.id);
          }}>
            <DownloadIcon fontSize="small" sx={{ mr: 1 }} />
            Download Invoice
          </MenuItem>
          {enableWhatsappShare && (
            <MenuItem onClick={handleShareWhatsApp}>
              <ShareIcon fontSize="small" sx={{ mr: 1 }} />
              Share via WhatsApp
            </MenuItem>
          )}
          <MenuItem
            onClick={() => handleProcessReturn(selectedOrderId)}
            disabled={!orders.find(o => o.id === selectedOrderId)?.status?.includes('DELIVERED')}
          >
            <ReturnIcon fontSize="small" sx={{ mr: 1 }} />
            Process Return
          </MenuItem>
          <MenuItem onClick={() => {
            const order = orders.find(o => o.id === selectedOrderId);
            handleMenuClose();
            if (order) handleOpenStatusDialog(order);
          }}>
            <UpdateIcon fontSize="small" sx={{ mr: 1 }} />
            Update Status
          </MenuItem>
          <MenuItem onClick={() => {
            const order = orders.find(o => o.id === selectedOrderId);
            handleMenuClose();
            if (order) handleOpenPaymentStatusDialog(order);
          }}>
            <UpdateIcon fontSize="small" sx={{ mr: 1 }} />
            Update Payment Status
          </MenuItem>
        </Menu>

        {/* Status Update Dialog */}
        <Dialog
          open={statusDialogOpen}
          onClose={handleCloseStatusDialog}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>
            Update Order Status
            {selectedOrderForStatus && (
              <Typography variant="subtitle2" color="textSecondary">
                Order #{selectedOrderForStatus.id}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="PROCESSING">Processing</MenuItem>
                <MenuItem value="SHIPPED">Shipped</MenuItem>
                <MenuItem value="DELIVERED">Delivered</MenuItem>
                <MenuItem value="CANCELLED">Cancelled</MenuItem>
              </Select>
              <FormHelperText>
                Current Status: {selectedOrderForStatus?.status}
              </FormHelperText>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseStatusDialog}>Cancel</Button>
            <Button
              onClick={handleUpdateStatus}
              variant="contained"
              disabled={!selectedStatus || loading || selectedStatus === selectedOrderForStatus?.status}
            >
              {loading ? 'Updating...' : 'Update'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Payment Status Update Dialog */}
        <Dialog
          open={paymentStatusDialogOpen}
          onClose={handleClosePaymentStatusDialog}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>
            Update Payment Status
            {selectedOrderForPaymentStatus && (
              <Typography variant="subtitle2" color="textSecondary">
                Order #{selectedOrderForPaymentStatus.id}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Payment Status</InputLabel>
              <Select
                value={selectedPaymentStatus}
                onChange={(e) => setSelectedPaymentStatus(e.target.value)}
                label="Payment Status"
              >
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="PARTIAL">Partial</MenuItem>
                <MenuItem value="PAID">Paid</MenuItem>
                <MenuItem value="REFUNDED">Refunded</MenuItem>
              </Select>
              <FormHelperText>
                Current Payment Status: {selectedOrderForPaymentStatus?.paymentStatus}
              </FormHelperText>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClosePaymentStatusDialog}>Cancel</Button>
            <Button
              onClick={handleUpdatePaymentStatus}
              variant="contained"
              disabled={!selectedPaymentStatus || loading || selectedPaymentStatus === selectedOrderForPaymentStatus?.paymentStatus}
            >
              {loading ? 'Updating...' : 'Update'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Receipt Dialog - COMMENTED OUT FOR FUTURE USE
        Dialog is now hidden - invoice downloads directly when clicking "Download Invoice"
        To re-enable: uncomment this dialog and change handleOpenReceiptDialog back to opening dialog */}
        {false && (
          <Dialog
            open={receiptDialogOpen}
            onClose={() => setReceiptDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              Download Invoice
              <IconButton
                aria-label="close"
                onClick={() => setReceiptDialogOpen(false)}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body1" gutterBottom>
                Select the type of invoice(s) you want to download:
              </Typography>
              <FormGroup sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedReceiptTypes.CUSTOMER}
                      onChange={handleReceiptTypeChange}
                      name="CUSTOMER"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PersonIcon sx={{ mr: 1, fontSize: 20 }} />
                      For Customer
                    </Box>
                  }
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedReceiptTypes.OFFICE}
                      onChange={handleReceiptTypeChange}
                      name="OFFICE"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <BusinessIcon sx={{ mr: 1, fontSize: 20 }} />
                      For Office
                    </Box>
                  }
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedReceiptTypes.OFFICE_WITH_SIGNATURE}
                      onChange={handleReceiptTypeChange}
                      name="OFFICE_WITH_SIGNATURE"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AssignmentIcon sx={{ mr: 1, fontSize: 20 }} />
                      For Office with Customer Signature
                    </Box>
                  }
                />
              </FormGroup>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setReceiptDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<PrintIcon />}
                onClick={handlePrintReceipt}
                disabled={!Object.values(selectedReceiptTypes).some(Boolean)}
                color="primary"
              >
                Download Selected Invoices
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Edit Order Dialog */}
        <Dialog
          open={editOrderDialogOpen}
          onClose={() => setEditOrderDialogOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            Edit Order
            {selectedOrderForEdit && (
              <Typography variant="subtitle2" color="textSecondary">
                Order #{selectedOrderForEdit.orderNumber || selectedOrderForEdit.id}
              </Typography>
            )}
            <IconButton
              aria-label="close"
              onClick={() => setEditOrderDialogOpen(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {editOrderLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : selectedOrderForEdit ? (
              <>
                {/* Order Info */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" gutterBottom>
                      <strong>Customer:</strong> {selectedOrderForEdit.customer?.name || selectedOrderForEdit.customerName}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Order Date:</strong> {formatOrderDate(selectedOrderForEdit.orderDate)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Payment Method:</strong> {formatPaymentMethod(selectedOrderForEdit.paymentMethod)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Status:</strong> {selectedOrderForEdit.status}
                    </Typography>
                  </Grid>
                </Grid>

                {/* Editable Items */}
                <Typography variant="h6" gutterBottom>
                  Order Items
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell align="right">Unit Price</TableCell>
                        <TableCell align="center">Quantity</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {editOrderItems.map((item, index) => (
                        <TableRow key={item.id || index}>
                          <TableCell>
                            <Typography variant="body2">
                              {item.product?.name || item.productName || 'Unknown Product'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            ₹{(item.unitPrice || 0).toFixed(2)}
                          </TableCell>
                          <TableCell align="center">
                            <TextField
                              type="number"
                              size="small"
                              value={item.quantity || 0}
                              onChange={(e) => handleEditItemQuantity(index, parseInt(e.target.value) || 0)}
                              inputProps={{ min: 0, max: 9999 }}
                              sx={{ width: 80 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            ₹{((item.unitPrice || 0) * (item.quantity || 0)).toFixed(2)}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteItem(index)}
                              title="Remove item"
                            >
                              <RemoveCircleIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                      {editOrderItems.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            <Typography variant="body2" color="textSecondary">
                              No items in this order
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Order Totals */}
                <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                      <Typography variant="body2" color="textSecondary">
                        * Changes will update inventory and credit balances automatically
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body1">
                          <strong>Subtotal: ₹{calculateEditSubtotal().toFixed(2)}</strong>
                        </Typography>
                        <Typography variant="h6" color="primary">
                          <strong>Total: ₹{calculateEditTotal().toFixed(2)}</strong>
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditOrderDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveEditOrder}
              disabled={editOrderLoading || editOrderItems.length === 0}
              color="primary"
            >
              {editOrderLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default ViewOrdersPage; 